/**
 * Conversation — 1-on-1 DM chat view.
 * Route: /messages/:conversationId
 *
 * Redesigned with gradient bubbles, glass panels, f8 brief banner, MonoAvatar.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, MoreVertical, Slash, User, AlertTriangle, Smile, Loader, Send } from 'react-feather';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { draftMessageEurW, getDraftQuotaEurW } from '../firebaseConfig';

interface DraftQuota {
  isPremium: boolean;
  limit: number;
  used: number;
  remaining: number | null;
}
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import MonoAvatar from '../components/MonoAvatar';
import {
  subscribeToMessages,
  sendMessage,
  markChatSeen,
  addReaction,
  setTyping,
  clearTyping,
  subscribeToTyping,
  checkBlockStatus,
  blockUser,
  getChatId,
  getConversations,
  respondToMessageRequest,
} from '../services/messageService';
import type { ChatMessage, TypingStatus, BlockStatus } from '../types/social';
import { forgetRequest, markPrompted, useMessageRequests } from '../hooks/useMessageRequests';
import { trackRequestAnswered } from '../components/messageRequestAnalytics';
import '../styles/messageRequest.css';
import { ALLOWED_EMOJIS } from '../types/social';
import { toDateSafe } from '../utils/dateUtils';

function formatTime(ts: any): string {
  const d = toDateSafe(ts);
  if (!d) return '';
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function daysSinceLastMessage(messages: ChatMessage[], recipientId: string): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].senderId === recipientId) {
      const d = toDateSafe(messages[i].timestamp);
      if (d) return Math.floor((Date.now() - d.getTime()) / 86400000);
    }
  }
  return null;
}

function shouldShowTimestamp(msg: ChatMessage, prevMsg: ChatMessage | undefined): boolean {
  if (!prevMsg) return true;
  const a = toDateSafe(msg.timestamp);
  const b = toDateSafe(prevMsg.timestamp);
  if (!a || !b) return true;
  return Math.abs(a.getTime() - b.getTime()) > 300000; // 5 min gap
}

interface ConversationProps {
  conversationId?: string;
  recipientId?: string;
  recipientName?: string;
  onBack?: () => void;
}

export default function Conversation({
  conversationId: propConversationId,
  recipientId: propRecipientId,
  recipientName: propRecipientName,
  onBack,
}: ConversationProps = {}) {
  const params = useParams<{ conversationId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Props take priority over URL params / location state
  const recipientId = propRecipientId || (location.state as any)?.recipientId || '';
  const recipientName = propRecipientName || (location.state as any)?.recipientName || 'User';
  const resolvedConversationId = propConversationId || params.conversationId || '';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState('');
  const [draftQuota, setDraftQuota] = useState<DraftQuota | null>(null);
  const [blockStatus, setBlockStatus] = useState<BlockStatus | null>(null);
  const [typingUsers, setTypingUsers] = useState<Record<string, TypingStatus>>({});
  const [reactionPickerMsg, setReactionPickerMsg] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  /**
   * PPL-428/429 — the request status of THIS conversation. `null` while it is
   * unknown, and an ordinary conversation everywhere else: a chat from before
   * requests existed has no status at all, and the server never reports a
   * declined one to the person who was declined.
   */
  const [chatStatus, setChatStatus] = useState<{ status?: string; requestedBy?: string | null } | null>(null);
  const [answering, setAnswering] = useState(false);

  // The app-wide prompt can answer this very conversation while the page is
  // open — it navigates to the same URL, so the status effect never re-runs.
  // Once the chat leaves the shared request list, the banner has no business
  // still offering accept and decline.
  const {requests: pendingRequests, loaded: requestsLoaded} = useMessageRequests();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatId = resolvedConversationId;

  const autoGrowInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 160; // cap ~6 lines, then scroll
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    autoGrowInput();
  }, [input, autoGrowInput]);

  useEffect(() => {
    if (!recipientId) return;
    checkBlockStatus(recipientId).then(setBlockStatus).catch(console.error);
  }, [recipientId]);

  useEffect(() => {
    if (!user?.uid) return;
    getDraftQuotaEurW({})
      .then((res) => setDraftQuota(res.data as DraftQuota))
      .catch((err) => console.error('Draft quota fetch failed:', err));
  }, [user?.uid]);

  useEffect(() => {
    if (!chatId || !user?.uid) return;
    markChatSeen(chatId).catch(console.error);
    // The conversation list is where status lives; one read on open is enough,
    // and a reply flips it server-side anyway.
    // One page is enough in practice: a conversation with a pending request has
    // just been written to, so it sorts to the top of this list. If it somehow
    // is not here, the fallback below treats it as an ordinary conversation —
    // the banner is missing rather than wrong.
    getConversations(40)
      .then((res) => {
        const found = res.conversations.find((c) => c.chatId === chatId);
        setChatStatus(found ? { status: found.status, requestedBy: found.requestedBy } : { status: 'accepted' });
      })
      .catch(() => setChatStatus({ status: 'accepted' }));

    const unsub = subscribeToMessages(chatId, setMessages, (err) =>
      console.error('Message listener error:', err)
    );
    return unsub;
  }, [chatId, user?.uid]);

  useEffect(() => {
    if (!chatId) return;
    const unsub = subscribeToTyping(chatId, setTypingUsers);
    return unsub;
  }, [chatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTyping = useCallback(() => {
    if (!chatId || !recipientId) return;
    setTyping(chatId, recipientId).catch(() => {});
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      clearTyping(chatId, recipientId).catch(() => {});
    }, 3000);
  }, [chatId, recipientId]);

  const isRecipientTyping = recipientId && typingUsers[recipientId]?.isTyping;
  const isBlocked = blockStatus?.currentUserBlockedOther || blockStatus?.otherUserBlockedCurrent;

  // Who is looking at a pending request: the person who was asked sees the
  // banner, the person who asked sees that they are waiting. Everyone else
  // (and every conversation from before requests) sees neither.
  const stillPending = pendingRequests.some((r) => r.chatId === chatId);
  // The conversation's own status is what shows the banner; the request list
  // is only allowed to TAKE it away, once it has actually loaded. Otherwise a
  // deep link into a fresh request (or a failed first poll) opens a
  // conversation the recipient cannot answer.
  const answeredElsewhere = requestsLoaded && !stillPending;
  const isRequest = chatStatus?.status === 'requested' &&
    (chatStatus?.requestedBy === user?.uid || !answeredElsewhere);
  const iAmRequester = isRequest && chatStatus?.requestedBy === user?.uid;
  const iWasAsked = isRequest && !iAmRequester;

  async function answerRequest(action: 'accept' | 'decline') {
    if (!chatId || answering) return;
    setAnswering(true);
    setError('');
    try {
      const {status, changed} = await respondToMessageRequest(chatId, action);
      // PPL-432: the server's answer, and only when it changed something —
      // the same request answered again from another device is one accept.
      if (changed) {
        trackRequestAnswered(
          status === 'accepted' ? 'accept' : status === 'declined' ? 'decline' : action,
          'thread'
        );
      }
      // Answered here, so the app-wide prompt must not offer it again before
      // the poller next runs.
      forgetRequest(chatId);
      markPrompted(chatId);
      if (action === 'accept') {
        setChatStatus({ status: 'accepted', requestedBy: chatStatus?.requestedBy });
      } else {
        navigate('/messages');
      }
    } catch {
      setError("couldn't do that — it's still waiting in requests");
    } finally {
      setAnswering(false);
    }
  }

  async function handleSend() {
    if (!input.trim() || sending || !chatId) return;
    setSending(true);
    setError('');
    try {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      clearTyping(chatId, recipientId).catch(() => {});
      const res = await sendMessage(input.trim(), recipientId, chatId);
      if (!res.success) {
        setError(res.reason || 'Failed to send message');
      } else {
        setInput('');
        // A reply from the person who was asked IS the acceptance (the server
        // stamps it), so the banner has to go with the message — and it counts
        // as an accept from the thread, or replying instead of pressing the
        // button would look like the request was never answered.
        if (iWasAsked) {
          trackRequestAnswered('accept', 'thread');
          setChatStatus({ status: 'accepted', requestedBy: chatStatus?.requestedBy });
          forgetRequest(chatId);
          markPrompted(chatId);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  }

  async function handleReaction(messageId: string, emoji: string) {
    try {
      await addReaction(chatId, messageId, emoji);
    } catch (err) {
      console.error('Reaction error:', err);
    }
    setReactionPickerMsg(null);
  }

  function isLimitReached() {
    return !!draftQuota && !draftQuota.isPremium && (draftQuota.remaining ?? 0) <= 0;
  }

  async function handleDraftRequest() {
    if (drafting || sending) return;
    if (isLimitReached()) {
      navigate('/premium');
      return;
    }
    const name = (recipientName || '').split(' ')[0];
    if (!name) return;
    setDrafting(true);
    setError('');
    try {
      const res = await draftMessageEurW({ recipientName: name, recipientId });
      const data = res.data as { draft?: string; quota?: DraftQuota };
      if (data?.draft) {
        setInput(data.draft);
      } else {
        setError('Could not generate a draft');
      }
      if (data?.quota) setDraftQuota(data.quota);
    } catch (err: any) {
      const code = err?.code || err?.details?.code;
      if (code === 'functions/resource-exhausted' || code === 'resource-exhausted') {
        setDraftQuota((q) => q ? { ...q, used: q.limit, remaining: 0 } : q);
        setError('You\'ve used all your free drafts. Upgrade to premium for unlimited.');
      } else {
        setError(err?.message || 'Failed to draft message');
      }
    } finally {
      setDrafting(false);
    }
  }

  async function handleBlock() {
    if (!recipientId) return;
    // Blocking instead of answering IS the answer, so it has to retire the
    // request the way the prompt card's block does. Without the decline the
    // chat stays `requested`: the app-wide prompt pops the card for the person
    // who was just blocked, and answering it there would count that one
    // request a second time (PPL-432).
    const blockAnswersRequest = iWasAsked;
    try {
      await blockUser(recipientId);
      // Only when there is a request to block instead of answering: blocking
      // someone in a conversation you already accepted is not this event.
      if (blockAnswersRequest) {
        trackRequestAnswered('block', 'thread');
        // Best-effort, exactly like the card: the block is what was promised,
        // and a decline that fails must not undo it. One dm_request_blocked,
        // never also a dm_request_declined.
        await respondToMessageRequest(chatId, 'decline').catch(() => {});
        forgetRequest(chatId);
        markPrompted(chatId);
        setChatStatus({ status: 'declined', requestedBy: chatStatus?.requestedBy });
      }
      setBlockStatus({
        isBlocked: true,
        currentUserBlockedOther: true,
        otherUserBlockedCurrent: blockStatus?.otherUserBlockedCurrent || false,
      });
      setShowMenu(false);
    } catch (err) {
      console.error('Block error:', err);
    }
  }


  const isEmbedded = !!propConversationId;

  const content = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: isEmbedded ? '100%' : 'calc(100vh - 60px)',
      maxWidth: isEmbedded ? undefined : 780,
      margin: isEmbedded ? undefined : '0 auto',
      padding: isEmbedded ? 0 : '0 16px',
      minHeight: 0,
    }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isEmbedded ? '14px 24px' : '14px 0',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => onBack ? onBack() : navigate('/messages')}
              style={{
                appearance: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(220,200,240,0.85)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(217,70,239,0.06)';
                e.currentTarget.style.borderColor = 'rgba(217,70,239,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
              }}
            >
              <ArrowLeft size={16} />
            </button>
            <MonoAvatar name={recipientName} size={36} />
            <div>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{recipientName}</span>
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              style={{
                appearance: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(220,200,240,0.85)',
                cursor: 'pointer',
              }}
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 6,
                background: 'rgba(28,16,42,0.96)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 14,
                backdropFilter: 'blur(24px)',
                overflow: 'hidden',
                zIndex: 100,
                minWidth: 180,
                boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
              }}>
                {!blockStatus?.currentUserBlockedOther && (
                  <button
                    onClick={handleBlock}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      background: 'transparent',
                      border: 'none',
                      color: '#ff6b6b',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Slash size={14} /> block user
                  </button>
                )}
                <button
                  onClick={() => { setShowMenu(false); navigate(`/user/${recipientId}`); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(220,200,240,0.85)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <User size={14} /> view profile
                </button>
              </div>
            )}
          </div>
        </div>

        {/* f8 brief banner */}
        {recipientId && !isBlocked && (
          <button
            onClick={handleDraftRequest}
            disabled={drafting || sending}
            style={{
              appearance: 'none',
              cursor: drafting ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              margin: isEmbedded ? '10px 24px 0' : '10px 0 0',
              borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(217,70,239,0.10), rgba(168,85,247,0.10))',
              border: '1px solid rgba(217,70,239,0.22)',
              color: '#f0abfc',
              fontSize: 12,
              fontFamily: 'inherit',
              textAlign: 'left',
              flexShrink: 0,
              opacity: drafting ? 0.7 : 1,
              transition: 'background 0.12s, border-color 0.12s, opacity 0.12s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217,70,239,0.18), rgba(168,85,247,0.18))';
              e.currentTarget.style.borderColor = 'rgba(217,70,239,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217,70,239,0.10), rgba(168,85,247,0.10))';
              e.currentTarget.style.borderColor = 'rgba(217,70,239,0.22)';
            }}
          >
            {drafting ? (
              <Loader size={12} className="spin" />
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="currentColor"/>
              </svg>
            )}
            <span style={{ flex: 1 }}>
              {drafting
                ? 'drafting a message…'
                : isLimitReached()
                  ? 'free drafts used up — upgrade for unlimited'
                  : `ask f8 to draft a message to ${(recipientName || 'them').split(' ')[0]}`}
            </span>
            {!drafting && draftQuota && !draftQuota.isPremium && (
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 999,
                background: isLimitReached() ? 'rgba(217,70,239,0.35)' : 'rgba(217,70,239,0.18)',
                color: '#fff',
                whiteSpace: 'nowrap',
              }}>
                {isLimitReached()
                  ? 'upgrade'
                  : `${draftQuota.remaining}/${draftQuota.limit} left`}
              </span>
            )}
            {!drafting && draftQuota?.isPremium && (
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 999,
                background: 'rgba(217,70,239,0.18)',
                color: '#fff',
              }}>
                premium
              </span>
            )}
          </button>
        )}

        {/* Messages area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: isEmbedded ? '20px 24px' : '20px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          {messages.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: 'rgba(220,200,240,0.35)',
              fontSize: 14,
            }}>
              <p>start the conversation — say hello!</p>
            </div>
          )}

          {messages.map((msg, idx) => {
            const isMine = msg.senderId === user?.uid;
            const prevMsg = messages[idx - 1];
            const showStamp = shouldShowTimestamp(msg, prevMsg);

            return (
              <div key={msg.id}>
                {showStamp && (
                  <div style={{
                    textAlign: 'center',
                    fontSize: 10.5,
                    color: 'rgba(220,200,240,0.3)',
                    padding: '12px 0 6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontWeight: 600,
                  }}>
                    {formatTime(msg.timestamp)}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexDirection: isMine ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    marginBottom: 2,
                    position: 'relative',
                  }}
                >
                  {!isMine && (
                    <div style={{ width: 26, flexShrink: 0 }}>
                      {(!prevMsg || prevMsg.senderId === user?.uid) && (
                        <MonoAvatar name={recipientName} size={26} />
                      )}
                    </div>
                  )}
                  <div style={{
                    maxWidth: '70%',
                    padding: '9px 13px',
                    borderRadius: 16,
                    borderTopRightRadius: isMine ? 4 : 16,
                    borderTopLeftRadius: isMine ? 16 : 4,
                    background: isMine
                      ? 'linear-gradient(135deg, #d946ef, #a855f7)'
                      : 'rgba(255,255,255,0.05)',
                    border: isMine ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    boxShadow: isMine ? '0 6px 20px rgba(217,70,239,0.25)' : 'none',
                    color: '#fff',
                    position: 'relative',
                    ...(msg.isSpam ? { opacity: 0.6 } : {}),
                  }}>
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>{msg.text}</p>
                    {msg.isSpam && (
                      <span title={msg.spamWarning || 'Flagged'} style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        color: '#ff6b6b',
                      }}>
                        <AlertTriangle size={12} />
                      </span>
                    )}
                  </div>

                  {/* Reaction trigger */}
                  {!isMine && (
                    <button
                      onClick={() => setReactionPickerMsg(reactionPickerMsg === msg.id ? null : msg.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(220,200,240,0.25)',
                        cursor: 'pointer',
                        opacity: 0.4,
                        transition: 'opacity 0.15s',
                        padding: 4,
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.4')}
                    >
                      <Smile size={14} />
                    </button>
                  )}
                </div>

                {/* Reactions */}
                {msg.reactions && msg.reactions.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: 4,
                    marginTop: 2,
                    justifyContent: isMine ? 'flex-end' : 'flex-start',
                    paddingLeft: isMine ? 0 : 34,
                  }}>
                    {msg.reactions.map((r, i) => (
                      <span key={i} style={{
                        fontSize: 14,
                        padding: '2px 6px',
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.06)',
                      }}>
                        {r.emoji}
                      </span>
                    ))}
                  </div>
                )}

                {/* Reaction picker */}
                {reactionPickerMsg === msg.id && (
                  <div style={{
                    display: 'flex',
                    gap: 4,
                    marginTop: 4,
                    marginLeft: 34,
                    padding: '6px 10px',
                    borderRadius: 12,
                    background: 'rgba(28,16,42,0.96)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(16px)',
                    width: 'fit-content',
                  }}>
                    {ALLOWED_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(msg.id, emoji)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: 18,
                          cursor: 'pointer',
                          padding: '2px 4px',
                          borderRadius: 6,
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {isRecipientTyping && (
            <div style={{
              display: 'flex',
              gap: 4,
              alignItems: 'center',
              padding: '8px 14px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
              width: 'fit-content',
              marginLeft: 34,
            }}>
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* PPL-429 — the person who was asked decides here, or simply replies:
            typing an answer accepts, so the composer stays usable. */}
        {iWasAsked && !isBlocked && (
          <div className="mr-banner">
            <span className="mr-banner-text">wants to send you a message</span>
            <div className="mr-banner-actions">
              <button
                className="mr-banner-btn mr-banner-btn--primary"
                onClick={() => answerRequest('accept')}
                disabled={answering}
              >
                accept
              </button>
              <button className="mr-banner-btn" onClick={() => answerRequest('decline')} disabled={answering}>
                decline
              </button>
            </div>
          </div>
        )}

        {/* Input area */}
        {iAmRequester && !isBlocked ? (
          // They have had their one message. Nothing here says whether it was
          // declined — the requester is never told.
          <div className="mr-waiting">waiting for {recipientName.startsWith('@') ? recipientName : `@${recipientName}`}</div>
        ) : isBlocked ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '16px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            color: 'rgba(220,200,240,0.35)',
            fontSize: 13,
            flexShrink: 0,
          }}>
            <Slash size={16} />
            <span>
              {blockStatus?.currentUserBlockedOther
                ? 'You blocked this user'
                : 'You cannot message this user'}
            </span>
          </div>
        ) : (
          <div style={{
            padding: isEmbedded ? '12px 20px 20px' : '12px 0 16px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0,
          }}>
            {error && (
              <div style={{
                padding: '8px 12px',
                marginBottom: 8,
                borderRadius: 10,
                background: 'rgba(255,107,107,0.12)',
                border: '1px solid rgba(255,107,107,0.2)',
                color: '#ff6b6b',
                fontSize: 13,
              }}>
                {error}
              </div>
            )}
            <div style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-end',
              padding: '10px 12px',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <textarea
                ref={inputRef}
                rows={1}
                placeholder={`Message ${recipientName.split(' ')[0]}...`}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  handleTyping();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  background: 'transparent',
                  border: 0,
                  color: '#fff',
                  fontSize: 13.5,
                  fontFamily: 'inherit',
                  fontWeight: 400,
                  outline: 'none',
                  resize: 'none',
                  lineHeight: 1.45,
                  maxHeight: 160,
                  overflowY: 'hidden',
                }}
              />
              <button
                onClick={handleDraftRequest}
                disabled={drafting || sending}
                title="ask f8 to draft a message"
                style={{
                  appearance: 'none',
                  border: 0,
                  cursor: drafting ? 'default' : 'pointer',
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: 'rgba(217, 70, 239, 0.12)',
                  color: '#f0abfc',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'background 0.12s',
                  opacity: drafting ? 0.7 : 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(217, 70, 239, 0.22)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(217, 70, 239, 0.12)')}
              >
                {drafting ? (
                  <Loader size={11} className="spin" />
                ) : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="currentColor"/>
                  </svg>
                )}
                <span>
                  {drafting
                    ? 'Drafting…'
                    : isLimitReached()
                      ? 'Upgrade'
                      : 'Draft'}
                </span>
                {!drafting && draftQuota && !draftQuota.isPremium && !isLimitReached() && (
                  <span style={{
                    fontSize: 9,
                    opacity: 0.75,
                    fontWeight: 500,
                  }}>
                    {draftQuota.remaining}
                  </span>
                )}
              </button>
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                style={{
                  appearance: 'none',
                  border: 0,
                  cursor: !input.trim() || sending ? 'default' : 'pointer',
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: input.trim() && !sending
                    ? 'linear-gradient(135deg, #d946ef, #a855f7)'
                    : 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: input.trim() && !sending ? 1 : 0.4,
                  flexShrink: 0,
                  transition: 'all 0.15s ease',
                  boxShadow: input.trim() && !sending ? '0 4px 16px rgba(217,70,239,0.32)' : 'none',
                }}
              >
                {sending ? <Loader size={14} className="spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        )}
    </div>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <div className="feed">
      <SEO title={`Chat with @${recipientName} — pplkpr`} description="Direct message" path={`/messages/${chatId}`} />
      {content}
    </div>
  );
}
